import { useSigma } from '@react-sigma/core';
import { useEffect, useMemo } from 'react';

const hasNumber = (arr: unknown, needle: number): boolean => {
  if (!Array.isArray(arr)) return false;
  return arr.includes(needle);
};

type GraphDataControllerProps = {
  typeFilter: Set<string>;
  clusterFilter: Set<number>;
  searchMatches: Set<string>;
  documentId?: string;
  onVisibleCountsChange?: (counts: { nodes: number; edges: number }) => void;
};

export const GraphDataController = ({
  typeFilter,
  clusterFilter,
  searchMatches,
  documentId,
  onVisibleCountsChange,
}: GraphDataControllerProps) => {
  const sigma = useSigma();
  const graph = sigma.getGraph();

  const docIdNum = useMemo(() => {
    if (!documentId) return null;
    const n = Number(documentId);
    return Number.isFinite(n) ? n : null;
  }, [documentId]);

  useEffect(() => {
    const isTypeFiltering = typeFilter.size > 0;
    const isClusterFiltering = clusterFilter.size > 0;
    const isSearchFiltering = searchMatches.size > 0;

    const visibleNodes = new Set<string>();

    graph.forEachNode((nodeId, attrs) => {
      const nodeType = attrs.entityType;
      const nodeCluster = attrs.cluster as number;
      const nodeDocs = attrs.documents as unknown;

      const passesType = !isTypeFiltering || typeFilter.has(nodeType);
      const passesCluster = !isClusterFiltering || clusterFilter.has(nodeCluster);
      const passesDoc = docIdNum === null || hasNumber(nodeDocs, docIdNum);
      const passesSearch = !isSearchFiltering || searchMatches.has(nodeId);

      const visible = passesType && passesCluster && passesDoc && passesSearch;

      graph.setNodeAttribute(nodeId, 'hidden', !visible);

      if (visible) visibleNodes.add(nodeId);
    });

    let visibleEdges = 0;

    graph.forEachEdge((edgeId, attrs, source, target) => {
      const edgeDocs = attrs.documents as unknown;

      const visible =
        visibleNodes.has(source) &&
        visibleNodes.has(target) &&
        (docIdNum === null || hasNumber(edgeDocs, docIdNum));

      graph.setEdgeAttribute(edgeId, 'hidden', !visible);

      if (visible) visibleEdges += 1;
    });

    onVisibleCountsChange?.({
      nodes: visibleNodes.size,
      edges: visibleEdges,
    });

    // Refresh on next frame to reduce input jank
    requestAnimationFrame(() => {
      try {
        sigma.refresh();
      } catch {
        // Sigma can throw if it's been unmounted/disposed between frames.
        // Safe to ignore.
      }
    });
  }, [typeFilter, clusterFilter, searchMatches, docIdNum, graph, sigma, onVisibleCountsChange]);

  return null;
};
